import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText, Download, Loader2, Search, X, Upload as UploadIcon, ArrowDownToLine,
  MessageSquare, Paperclip, Car, Eye, Copy, CheckCircle2,
} from 'lucide-react';
import Pagination, { paginate, PAGE_SIZE } from './Pagination';
import OrderDetailDrawer from './OrderDetailDrawer';
import SortableHeader, { useTableSort } from './SortableHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function FileHistory({ token }) {
  const { t } = useTranslation('orders');
  const { t: tCommon } = useTranslation('common');
  const tNav = (k) => tCommon(`nav.${k}`);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  // Per-column filters mirror the old PHP File History page so customers
  // can quickly narrow by Order №, Vehicle (free-form), VIN or Status.
  const [filters, setFilters] = useState({ order_no: '', vehicle: '', vin: '', status: '' });
  const [page, setPage] = useState(1);
  // Client-side sort over the FULL filtered dataset (entire file history is
  // already loaded in one call; pagination is client-side). Filters first,
  // then sort, then pagination — matches the user's spec.
  const { sortBy, sortDir, onSortChange } = useTableSort({
    onPageReset: () => setPage(1),
  });
  const [openOrder, setOpenOrder] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  // Path-preview modal — for legacy rows where the storage isn't wired
  // up yet, the operator/customer can click to inspect the raw legacy
  // path and copy it to clipboard so they can fetch it manually.
  const [pathPreview, setPathPreview] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/my-files`, { headers });
      if (!r.ok) { setItems([]); return; }
      const j = await r.json();
      setItems(Array.isArray(j.items) ? j.items : []);
    } finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  // Aggregate by order_id => one row per order with original/modified/attachment info
  const orderRows = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const oid = it.order_id;
      if (!oid) continue;
      let row = map.get(oid);
      if (!row) {
        row = {
          order_id: oid,
          order_number: it.order_number || '—',
          vehicle: it.vehicle || '—',
          vehicle_type: it.vehicle_type || '',
          ecu_type: it.ecu_type || '',
          vin: it.vin || '',
          order_status: it.order_status || '',
          legacy_source: it.legacy_source || null,
          legacy_status: it.legacy_status || null,
          additional_info: it.additional_info || null,
          original: null,
          modified: null,
          ticket: null,
          attachments: 0,
          last_activity: it.uploaded_at,
          first_activity: it.uploaded_at,
        };
        map.set(oid, row);
      }
      if (it.kind === 'original') row.original = it;
      else if (it.kind === 'modified') row.modified = it;
      else if (it.kind === 'ticket') row.ticket = it;
      else if (it.kind === 'attachment') row.attachments += 1;
      if (it.uploaded_at) {
        if (!row.last_activity || it.uploaded_at > row.last_activity) row.last_activity = it.uploaded_at;
        if (!row.first_activity || it.uploaded_at < row.first_activity) row.first_activity = it.uploaded_at;
      }
      // Prefer concrete vehicle when populated later
      if ((!row.vehicle || row.vehicle === '—') && it.vehicle) row.vehicle = it.vehicle;
      if (!row.order_number || row.order_number === '—') row.order_number = it.order_number || row.order_number;
      if (!row.additional_info && it.additional_info) row.additional_info = it.additional_info;
    }
    const rows = Array.from(map.values());
    rows.sort((a, b) => (b.last_activity || '').localeCompare(a.last_activity || ''));
    return rows;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const fOrder = filters.order_no.trim().toLowerCase();
    const fVehicle = filters.vehicle.trim().toLowerCase();
    const fVin = filters.vin.trim().toLowerCase();
    const fStatus = filters.status;
    return orderRows.filter((r) => {
      if (fOrder && !(r.order_number || '').toLowerCase().includes(fOrder)) return false;
      if (fVehicle) {
        const hay = [r.vehicle, r.vehicle_type, r.ecu_type].filter(Boolean).join(' ').toLowerCase();
        // Split on whitespace so "BMW EDC" matches even when terms appear
        // in different fields. Every term must match.
        const terms = fVehicle.split(/\s+/).filter(Boolean);
        if (!terms.every((t) => hay.includes(t))) return false;
      }
      if (fVin && !(r.vin || '').toLowerCase().includes(fVin)) return false;
      if (fStatus && r.order_status !== fStatus) return false;
      if (q) {
        const hay = [
          r.order_number, r.vehicle, r.vehicle_type, r.ecu_type, r.vin,
          r.original?.name, r.modified?.name,
          r.original?.legacy_path, r.modified?.legacy_path,
          r.additional_info,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orderRows, query, filters]);

  // Available statuses from the dataset — drives the Status filter
  // dropdown so we never hard-code a partial list.
  const availableStatuses = useMemo(() => {
    const s = new Set();
    for (const r of orderRows) if (r.order_status) s.add(r.order_status);
    return Array.from(s).sort();
  }, [orderRows]);

  const pageRows = useMemo(() => {
    let list = filtered;
    if (sortBy) {
      // File-history is aggregated — `last_activity` is the canonical
      // date column, `original.size` the canonical size column. Map the
      // sortable token to the corresponding field on the aggregated row.
      const getter = {
        date:         (r) => r.last_activity || r.first_activity,
        order_number: (r) => r.order_number || '',
        file_name:    (r) => (r.original?.name || r.modified?.name || '').toLowerCase(),
        vehicle:      (r) => (r.vehicle || '').toLowerCase(),
        ecu_type:     (r) => (r.ecu_type || '').toLowerCase(),
        status:       (r) => (r.order_status || '').toLowerCase(),
        size:         (r) => Number(r.original?.size || r.modified?.size || 0),
      }[sortBy];
      if (getter) {
        const dir = sortDir === 'desc' ? -1 : 1;
        list = [...filtered].sort((a, b) => {
          const va = getter(a), vb = getter(b);
          if (va == null || va === '') return 1;       // nulls last
          if (vb == null || vb === '') return -1;
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
          if (va < vb) return -1 * dir;
          if (va > vb) return  1 * dir;
          return 0;
        });
      }
    }
    return paginate(list, page);
  }, [filtered, page, sortBy, sortDir]);
  useEffect(() => { setPage(1); }, [query]);

  // Counts reflect the current filter set so the customer always sees
  // how the cards relate to the rows below.
  const counts = useMemo(() => ({
    orders: filtered.length,
    originals: filtered.filter((r) => r.original).length,
    modified: filtered.filter((r) => r.modified).length,
    attachments: filtered.reduce((s, r) => s + r.attachments, 0),
  }), [filtered]);

  const downloadFile = async (it) => {
    if (!it) return;
    // Legacy preserved path with base URL configured → direct download
    // (no auth header on the proxied static host, browser handles it).
    if (it.legacy_path_preserved && it.download_url && /^https?:\/\//i.test(it.download_url)) {
      window.open(it.download_url, '_blank', 'noopener');
      return;
    }
    if (!it.download_url) return; // disabled state
    try {
      const r = await fetch(`${API_URL}${it.download_url}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = it.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed: ' + e.message); }
  };

  const openOrderById = async (orderId) => {
    if (!orderId) return;
    setOpeningId(orderId);
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const order = await r.json();
      setOpenOrder(order);
    } catch (e) {
      alert('Failed to open order: ' + e.message);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 w-full lg:w-[70%] mx-auto" data-testid="file-history-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">{tCommon('nav.file_history', { defaultValue: 'File History' })}</h1>
            <p className="text-sm text-slate-600">{t('subtitle_history', { defaultValue: 'All your orders with quick access to original / modified files and chat' })}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl text-white p-4 shadow-md bg-gradient-to-br from-slate-700 to-slate-800">
          <p className="text-[11px] uppercase font-bold opacity-80 tracking-wider">{tCommon('nav.my_orders')}</p>
          <p className="text-2xl font-bold mt-1" data-testid="stat-orders">{counts.orders}</p>
        </div>
        <div className="rounded-xl text-white p-4 shadow-md bg-gradient-to-br from-cyan-500 to-blue-600">
          <p className="text-[11px] uppercase font-bold opacity-80 tracking-wider">{t('actions.download_original')}</p>
          <p className="text-2xl font-bold mt-1" data-testid="stat-originals">{counts.originals}</p>
        </div>
        <div className="rounded-xl text-white p-4 shadow-md bg-gradient-to-br from-emerald-500 to-teal-600">
          <p className="text-[11px] uppercase font-bold opacity-80 tracking-wider">{t('actions.download_modified')}</p>
          <p className="text-2xl font-bold mt-1" data-testid="stat-modified">{counts.modified}</p>
        </div>
        <div className="rounded-xl text-white p-4 shadow-md bg-gradient-to-br from-amber-500 to-orange-600">
          <p className="text-[11px] uppercase font-bold opacity-80 tracking-wider">{t('chat_attachments', { defaultValue: 'Chat attachments' })}</p>
          <p className="text-2xl font-bold mt-1" data-testid="stat-attachments">{counts.attachments}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-5 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400 ml-2" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search_orders_placeholder', { defaultValue: 'Search by order number, vehicle, or file name…' })}
          data-testid="file-history-search"
          className="flex-1 px-2 py-1.5 text-sm focus:outline-none"
        />
        {query && (
          <button onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-600" data-testid="file-history-clear-search">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Table — desktop only */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-200 text-slate-700 text-[11px] font-bold uppercase tracking-wider">
                <SortableHeader label={t('columns.order', { defaultValue: 'Order #' })} field="order_number" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={t('columns.vehicle', { defaultValue: 'Vehicle' })} field="vehicle" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={t('columns_extra.date_created', { defaultValue: 'Date created', ns: 'tickets' })} field="date" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={t('columns.status', { defaultValue: 'Status' })} field="status" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-center whitespace-nowrap w-[320px]">{t('columns.actions')}</th>
              </tr>
              <tr className="bg-white border-b border-slate-200" data-testid="file-history-filters">
                <th className="px-3 py-2">
                  <input type="text" value={filters.order_no}
                    onChange={(e) => setFilters((f) => ({ ...f, order_no: e.target.value }))}
                    placeholder={t('filter_placeholder', { defaultValue: 'filter' })} data-testid="fh-filter-order-no"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-cyan-400 font-normal normal-case tracking-normal" />
                </th>
                <th className="px-3 py-2">
                  <input type="text" value={filters.vehicle}
                    onChange={(e) => setFilters((f) => ({ ...f, vehicle: e.target.value }))}
                    placeholder={t('vehicle_example_placeholder', { defaultValue: 'e.g. BMW EDC17' })} data-testid="fh-filter-vehicle"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-cyan-400 font-normal normal-case tracking-normal" />
                </th>
                <th className="px-3 py-2">
                  <input type="text" value={filters.vin}
                    onChange={(e) => setFilters((f) => ({ ...f, vin: e.target.value }))}
                    placeholder="VIN" data-testid="fh-filter-vin"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-cyan-400 font-normal normal-case tracking-normal" />
                </th>
                <th className="px-3 py-2">
                  <select value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    data-testid="fh-filter-status"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-cyan-400 font-normal normal-case tracking-normal">
                    <option value="">{t('file_history.filter_all', { defaultValue: 'All' })}</option>
                    {availableStatuses.map((s) => (
                      // Title-case the label (e.g. "completed" → "Completed")
                      // while keeping the raw status as the option value so
                      // the filter compares 1:1 against order.status.
                      <option key={s} value={s}>{t(`status.${s}`, { defaultValue: s.charAt(0).toUpperCase() + s.slice(1) })}</option>
                    ))}
                  </select>
                </th>
                <th className="px-3 py-2 text-right">
                  {(filters.order_no || filters.vehicle || filters.vin || filters.status) && (
                    <button onClick={() => setFilters({ order_no: '', vehicle: '', vin: '', status: '' })}
                      data-testid="fh-filter-clear"
                      className="text-[11px] text-slate-500 hover:text-slate-800 font-normal normal-case underline">
                      clear filters
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody data-testid="file-history-tbody">
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-600">
                    <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-500">
                    {orderRows.length === 0
                      ? 'No files yet — your uploaded originals, modified deliveries and chat attachments will appear here.'
                      : t('no_orders_match_search', { defaultValue: 'No orders match your search.' })}
                  </td>
                </tr>
              ) : (
                pageRows.map((row, idx) => {
                  const isOpening = openingId === row.order_id;
                  return (
                    <tr
                      key={row.order_id}
                      className={`border-b border-slate-100 hover:bg-cyan-50/60 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                      data-testid={`file-history-row-${idx}`}
                    >
                      {/* Col 1: Order number — clickable */}
                      <td className="px-2.5 py-3 whitespace-nowrap">
                        <button
                          onClick={() => openOrderById(row.order_id)}
                          disabled={isOpening}
                          className="font-mono text-sm font-bold text-cyan-700 hover:text-cyan-900 hover:underline disabled:opacity-50"
                          data-testid={`open-order-${row.order_number}`}
                          title="Open order"
                        >
                          {isOpening ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> : null}
                          {row.order_number}
                        </button>
                        {row.legacy_source === 'old_php_admin' && (
                          <span
                            className="ml-1 inline-block px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-violet-100 text-violet-700"
                            title={`Imported from old PHP admin · legacy id ${row.original?.legacy_order_id || row.modified?.legacy_order_id || ''}${row.legacy_status ? ` · legacy_status ${row.legacy_status}` : ''}`}
                            data-testid={`legacy-badge-${row.order_number}`}
                          >
                            legacy
                          </span>
                        )}
                      </td>

                      {/* Col 2: Vehicle */}
                      <td className="px-2.5 py-3 max-w-[280px]">
                        <div className="flex items-center gap-2 min-w-0">
                          <Car className="w-4 h-4 text-slate-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-slate-800 font-medium truncate" title={`${row.vehicle_type || ''} ${row.vehicle}${row.ecu_type ? ' · ' + row.ecu_type : ''}`}>
                              {row.vehicle_type && (
                                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mr-1">{row.vehicle_type}</span>
                              )}
                              {row.vehicle}
                              {row.ecu_type && (
                                <span className="ml-1 text-slate-500"> · {row.ecu_type}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Col 3: Date created */}
                      <td className="px-2.5 py-3 text-xs font-mono text-slate-600 whitespace-nowrap">
                        {fmtDate(row.first_activity)}
                      </td>

                      {/* Col 4: Status — mapped status with legacy original in tooltip */}
                      <td className="px-2.5 py-3 text-xs whitespace-nowrap" data-testid={`fh-status-${row.order_number}`}>
                        {row.order_status ? (
                          <span
                            title={row.legacy_status ? `Legacy status: ${row.legacy_status}` : ''}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                              ['completed', 'closed'].includes((row.order_status || '').toLowerCase())
                                ? 'bg-emerald-100 text-emerald-700'
                                : ['cancelled', 'canceled'].includes((row.order_status || '').toLowerCase())
                                  ? 'bg-rose-100 text-rose-700'
                                  : ['in_progress', 'processing'].includes((row.order_status || '').toLowerCase())
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {t(`status.${(row.order_status || '').toLowerCase()}`, { defaultValue: row.order_status.charAt(0).toUpperCase() + row.order_status.slice(1) })}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>

                      {/* Col 5: Actions */}
                      <td className="px-2.5 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2 flex-nowrap whitespace-nowrap">
                          {(() => {
                            // Original: native button OR legacy preserved path
                            const item = row.original;
                            const hasFile = !!item;
                            const legacy = !!item?.legacy_path_preserved;
                            const downloadable = !!item?.download_url;
                            const title = !hasFile ? 'No original file'
                              : legacy && !downloadable
                                ? `Legacy file path preserved (${item.legacy_path}), storage not connected yet`
                                : legacy
                                  ? `Download legacy original — ${item.legacy_path}`
                                  : `Download original — ${item.name} (${fmtBytes(item.size)})`;
                            return (
                              <span className="inline-flex">
                                <button
                                  onClick={() => downloadFile(item)}
                                  disabled={!hasFile || (legacy && !downloadable)}
                                  title={title}
                                  data-testid={`download-original-${idx}`}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide border bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-cyan-50 ${legacy ? 'rounded-l-lg' : 'rounded-lg'}`}
                                >
                                  <UploadIcon className="w-3.5 h-3.5" />
                                  {t('file_history.original_btn', { defaultValue: 'Original' })}
                                </button>
                                {legacy && (
                                  <button
                                    onClick={() => setPathPreview({ kind: 'Original', ...item, order_number: row.order_number, vehicle: row.vehicle })}
                                    title="Inspect legacy file path"
                                    data-testid={`preview-original-path-${idx}`}
                                    className="inline-flex items-center px-1.5 py-1.5 rounded-r-lg border border-l-0 bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </span>
                            );
                          })()}
                          {(() => {
                            // Modified: native (with engineer-release lock) OR legacy preserved.
                            const item = row.modified;
                            const legacy = !!item?.legacy_path_preserved;
                            const downloadable = !!item?.download_url;
                            const lockedFlag = !!item?.locked;
                            const lockTime = item?.locked_until ? new Date(item.locked_until) : null;
                            const lockedByTime = lockTime && lockTime.getTime() > Date.now();
                            const locked = lockedFlag || lockedByTime;
                            const lockLabel = lockTime
                              ? `${String(lockTime.getHours()).padStart(2,'0')}:${String(lockTime.getMinutes()).padStart(2,'0')}`
                              : '';
                            const reason = lockedByTime ? `Will be available at ${lockLabel}` : 'Awaiting engineer approval';
                            const disabled = !item || (legacy && !downloadable) || (!legacy && locked);
                            const title = !item ? 'Modified file not yet delivered'
                              : legacy && !downloadable
                                ? `Legacy file path preserved (${item.legacy_path}), storage not connected yet`
                                : legacy
                                  ? `Download legacy modified — ${item.legacy_path}`
                                  : locked ? reason
                                    : `Download modified — ${item.name} (${fmtBytes(item.size)})`;
                            const label = legacy ? t('file_history.modified_btn', { defaultValue: 'Modified' })
                              : locked ? (lockedByTime ? t('file_history.ready_at_label', { time: lockLabel, defaultValue: `Ready at ${lockLabel}` }) : t('file_history.awaiting', { defaultValue: 'Awaiting' }))
                                : t('file_history.modified_btn', { defaultValue: 'Modified' });
                            return (
                              <span className="inline-flex">
                                <button
                                  onClick={() => downloadFile(item)}
                                  disabled={disabled}
                                  title={title}
                                  data-testid={`download-modified-${idx}`}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-50 ${legacy ? 'rounded-l-lg' : 'rounded-lg'}`}
                                >
                                  <ArrowDownToLine className="w-3.5 h-3.5" />
                                  {label}
                                </button>
                                {legacy && item && (
                                  <button
                                    onClick={() => setPathPreview({ kind: 'Modified', ...item, order_number: row.order_number, vehicle: row.vehicle })}
                                    title="Inspect legacy file path"
                                    data-testid={`preview-modified-path-${idx}`}
                                    className="inline-flex items-center px-1.5 py-1.5 rounded-r-lg border border-l-0 bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </span>
                            );
                          })()}
                          <button
                            onClick={() => openOrderById(row.order_id)}
                            disabled={isOpening}
                            title={row.ticket?.legacy_ticket_id
                              ? `Open legacy ticket ${row.ticket.legacy_ticket_id}`
                              : 'Open ticket / chat for this order'}
                            data-testid={`open-chat-${idx}`}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide border ${
                              row.ticket
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                                : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                            } disabled:opacity-50`}
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            {t('file_history.ticket_btn', { defaultValue: 'Ticket' })}
                            {row.attachments > 0 && (
                              <span className="inline-flex items-center gap-0.5 ml-0.5 px-1 rounded bg-amber-100 text-amber-800 text-[10px]">
                                <Paperclip className="w-2.5 h-2.5" />{row.attachments}
                              </span>
                            )}
                          </button>
                        </div>
                        {row.additional_info && (
                          <details className="mt-2 text-[11px] text-slate-600"
                            data-testid={`additional-info-${idx}`}>
                            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                              {t('file_history.additional_info', { defaultValue: 'Additional info' })}
                            </summary>
                            <p className="mt-1 leading-relaxed whitespace-pre-wrap break-words text-slate-700">
                              {String(row.additional_info).replace(/<br\s*\/?>/gi, '\n')}
                            </p>
                          </details>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} label="orders" />
        )}
      </div>

      {/* Mobile card stack */}
      <div className="md:hidden space-y-3" data-testid="file-history-mobile-cards">
        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            {orderRows.length === 0
              ? 'No files yet — uploaded originals, modified deliveries and chat attachments will appear here.'
              : t('no_orders_match_search', { defaultValue: 'No orders match your search.' })}
          </div>
        )}
        {!loading && pageRows.map((row, idx) => {
          const isOpening = openingId === row.order_id;
          const lockedFlag = !!row.modified?.locked;
          const lockTime = row.modified?.locked_until ? new Date(row.modified.locked_until) : null;
          const lockedByTime = lockTime && lockTime.getTime() > Date.now();
          const locked = lockedFlag || lockedByTime;
          const lockLabel = lockTime
            ? `${String(lockTime.getHours()).padStart(2,'0')}:${String(lockTime.getMinutes()).padStart(2,'0')}`
            : 'Awaiting';
          return (
            <div
              key={row.order_id}
              data-testid={`file-history-card-${idx}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between gap-2">
                <button
                  onClick={() => openOrderById(row.order_id)}
                  disabled={isOpening}
                  data-testid={`open-order-mob-${row.order_number}`}
                  className="text-left min-w-0 flex-1"
                >
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Order №</p>
                  <p className="font-mono text-sm font-bold text-cyan-700 truncate">
                    {isOpening && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />}
                    {row.order_number}
                  </p>
                </button>
                {row.order_status && (
                  <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 flex-shrink-0">
                    {row.order_status}
                  </span>
                )}
              </div>
              <dl className="px-4 py-3 text-sm divide-y divide-slate-100">
                <div className="flex justify-between py-1.5 gap-3 items-start">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Vehicle</dt>
                  <dd className="text-slate-800 text-right min-w-0">
                    <div className="flex items-center justify-end gap-2">
                      <Car className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-medium break-words">{row.vehicle}</span>
                    </div>
                    {row.vehicle_type && (
                      <div className="text-[11px] text-slate-500 uppercase tracking-wider">{row.vehicle_type}</div>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Date</dt>
                  <dd className="text-slate-700 text-xs font-mono">{fmtDate(row.first_activity)}</dd>
                </div>
                {row.attachments > 0 && (
                  <div className="flex justify-between py-1.5 gap-3 items-center">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Attachments</dt>
                    <dd className="inline-flex items-center gap-1 text-amber-800 font-medium">
                      <Paperclip className="w-3.5 h-3.5" />{row.attachments}
                    </dd>
                  </div>
                )}
              </dl>
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 grid grid-cols-3 gap-2">
                <button
                  onClick={() => downloadFile(row.original)}
                  disabled={!row.original}
                  data-testid={`download-original-mob-${idx}`}
                  title={row.original ? `Download original — ${row.original.name} (${fmtBytes(row.original.size)})` : 'No original file'}
                  className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide border bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <UploadIcon className="w-3.5 h-3.5" />
                  Original
                </button>
                <button
                  onClick={() => downloadFile(row.modified)}
                  disabled={!row.modified || locked}
                  data-testid={`download-modified-mob-${idx}`}
                  title={
                    !row.modified ? 'Modified file not yet delivered'
                      : locked ? (lockedByTime ? `Will be available at ${lockLabel}` : 'Awaiting engineer approval')
                      : `Download modified — ${row.modified.name} (${fmtBytes(row.modified.size)})`
                  }
                  className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowDownToLine className="w-3.5 h-3.5" />
                  {locked ? lockLabel : 'Modified'}
                </button>
                <button
                  onClick={() => openOrderById(row.order_id)}
                  disabled={isOpening}
                  data-testid={`open-chat-mob-${idx}`}
                  title="Open ticket / chat for this order"
                  className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 disabled:opacity-50"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Ticket
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} label="orders" />
          </div>
        )}
      </div>

      {/* Order detail / chat drawer */}
      {openOrder && (
        <OrderDetailDrawer
          order={openOrder}
          token={token}
          isAdmin={false}
          onClose={() => { setOpenOrder(null); load(); }}
          onChanged={() => load()}
        />
      )}

      {/* Legacy file path preview / copy-to-clipboard modal. Surfaces the
          preserved path so operators can fetch the file from the old
          server manually until the storage is wired up. */}
      {pathPreview && (
        <LegacyPathPreviewModal item={pathPreview} onClose={() => setPathPreview(null)} />
      )}
    </div>
  );
}

function LegacyPathPreviewModal({ item, onClose }) {
  const [copied, setCopied] = useState(false);
  const path = item.legacy_path || '';
  const url = item.download_url || null;
  const doCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // navigator.clipboard fails on insecure origins — fallback
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500); }
      finally { document.body.removeChild(ta); }
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose} data-testid="legacy-path-modal">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl border border-slate-200"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Eye className="w-4 h-4 text-cyan-700" /> Legacy {item.kind} file path
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-4 space-y-3 text-xs">
          <div>
            <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-0.5">Order</p>
            <p className="font-mono">{item.order_number} · {item.vehicle}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-0.5">Preserved path</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-[11px] font-mono break-all"
                    data-testid="legacy-path-text">
                {path || '(empty)'}
              </code>
              <button onClick={() => doCopy(path)}
                disabled={!path}
                data-testid="legacy-path-copy"
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded bg-cyan-600 text-white text-[11px] font-bold hover:bg-cyan-700 disabled:opacity-40">
                {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
          </div>
          {url ? (
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-0.5">Full download URL</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-[11px] font-mono break-all text-emerald-900">
                  {url}
                </code>
                <a href={url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 px-2 py-1.5 rounded bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700">
                  <ArrowDownToLine className="w-3.5 h-3.5" /> Open
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 text-[11px] flex items-start gap-2">
              <span className="flex-shrink-0">⚠</span>
              <span>Legacy storage not connected yet. Configure
                <code className="mx-1 px-1 bg-amber-100 rounded text-[10px]">legacy_file_base_url</code>
                in Tools → Legacy Migration → Settings to enable the Open button here.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
